import clsx from "clsx";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

/**
 * room-type-reservation 用の丸ピルボタン（touch/surface 2層）。
 *
 * 外側 <button> が矩形のタップ領域（hit-zone）、内側 <span> が丸ピルの見た目（surface）。
 * 「見た目は丸のままタップ領域は矩形」を保つ。DxIconButton のテキスト版を、別 PJ と共有せず
 * このフィーチャー内 ui/ にローカル複製したもの。
 */
export type PillButtonVariant = "solid" | "ghost" | "disabled";
export type PillButtonSize = "small" | "medium";
/** ghost variant の枠線トーン（Figma: トグル=foreground/disabled, フィルター=border/default）。 */
export type PillButtonBorderTone = "default" | "muted";

type Props = {
  children: ReactNode;
  variant?: PillButtonVariant;
  size?: PillButtonSize;
  /** ghost のときのみ有効。default は "default"。 */
  borderTone?: PillButtonBorderTone;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  /** 外側のタップ領域 <button> に付与する追加クラス（min-w 上書き等）。 */
  className?: string;
  /** テスト識別用。intrinsic 要素と違い data-* が型で許容されないカスタム props のため明示定義する。 */
  "data-testid"?: string;
} & Pick<
  ComponentPropsWithoutRef<"button">,
  "onClick" | "disabled" | "type" | "aria-expanded" | "aria-label"
>;

const TOUCH_BASE =
  "group flex shrink-0 items-center justify-center " +
  "focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-40";

const TOUCH_SIZE: Record<PillButtonSize, string> = {
  small: "min-h-[32px]",
  medium: "h-[48px] min-w-[72px]",
};

const SURFACE_BASE =
  "font-notojp flex items-center justify-center gap-[4px] rounded-full " +
  "text-[14px] leading-[24px] font-bold whitespace-nowrap " +
  "transition-colors duration-150 " +
  "group-focus-visible:ring-2 group-focus-visible:ring-offset-1 group-focus-visible:ring-dx-border-focus";

const SURFACE_SIZE: Record<PillButtonSize, string> = {
  small: "min-w-[72px] px-[12px] py-[4px]",
  medium: "h-[40px] min-w-[72px] px-[16px]",
};

const SURFACE_VARIANT: Record<PillButtonVariant, string> = {
  solid:
    "bg-dx-bg-inverse text-dx-text-inverse group-hover:bg-dx-bg-inverse-hover",
  ghost:
    "bg-dx-surface-base text-dx-text-default border group-hover:bg-dx-bg-base-hover",
  disabled: "bg-dx-surface-subtle text-dx-text-muted",
};

const GHOST_BORDER: Record<PillButtonBorderTone, string> = {
  default: "border-dx-border-default",
  muted: "border-dx-text-muted",
};

type PillSurfaceProps = {
  children: ReactNode;
  variant?: PillButtonVariant;
  size?: PillButtonSize;
  borderTone?: PillButtonBorderTone;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
};

/**
 * 丸ピルの見た目部分（surface）のみを描画する非インタラクティブな `<span>`。
 * PillButton から切り出し、非ボタン用途（ReserveRoomTypePill 等）でも再利用する。
 */
export function PillSurface({
  children,
  variant = "solid",
  size = "medium",
  borderTone = "default",
  leadingIcon,
  trailingIcon,
}: PillSurfaceProps) {
  return (
    <span
      className={clsx(
        SURFACE_BASE,
        SURFACE_SIZE[size],
        SURFACE_VARIANT[variant],
        variant === "ghost" && GHOST_BORDER[borderTone],
      )}
    >
      {leadingIcon}
      {children}
      {trailingIcon}
    </span>
  );
}

export function PillButton({
  children,
  variant = "solid",
  size = "medium",
  borderTone = "default",
  leadingIcon,
  trailingIcon,
  className,
  type = "button",
  ...rest
}: Props) {
  return (
    <button
      type={type}
      className={clsx(TOUCH_BASE, TOUCH_SIZE[size], className)}
      {...rest}
    >
      <PillSurface
        variant={variant}
        size={size}
        borderTone={borderTone}
        leadingIcon={leadingIcon}
        trailingIcon={trailingIcon}
      >
        {children}
      </PillSurface>
    </button>
  );
}
